
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getAvatarUrl } from '@/utils/storage';
import { useProfileManager } from '@/hooks/useProfileManager';
import { useTranslation } from 'react-i18next';
import AvatarUpload from './profile/AvatarUpload';
import ProfileFormContainer from './profile/ProfileFormContainer';
import PersonalInfoForm from './profile/PersonalInfoForm';
import PreferencesForm from './profile/PreferencesForm';

interface ClientProfileProps {
  profile: any;
  setProfile: React.Dispatch<React.SetStateAction<any>>;
}

const ClientProfile: React.FC<ClientProfileProps> = ({ profile, setProfile }) => {
  const { user } = useAuth();
  const { t } = useTranslation('dashboard');
  const {
    error,
    isSaving,
    avatarFile,
    avatarPreview,
    
    handleAvatarChange,
    updateProfile
  } = useProfileManager(user?.id || '', profile);

  // Initialize avatar URL from profile
  const avatarUrl = profile?.avatar_url ? getAvatarUrl('client.photo', profile.avatar_url) : null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Extract form data
    const formData = new FormData(e.currentTarget);
    const values = {
      first_name: formData.get('first_name') as string,
      last_name: formData.get('last_name') as string,
      phone_number: formData.get('phone_number') as string,
      timezone: formData.get('timezone') as string,
      preferred_language: formData.get('preferred_language') as string,
      avatar_url: profile?.avatar_url
    };
    
    const success = await updateProfile(values);
    if (success) {
      // Update parent state to reflect changes
      setProfile((prev: any) => ({ ...prev, ...values }));
    }
  };

  return (
    <ProfileFormContainer 
      title={t('clientProfile.title')}
      error={error}
      isSubmitting={isSaving}
      hasAvatarFile={!!avatarFile}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col sm:flex-row items-center gap-6 mb-8">
        <AvatarUpload 
          avatarUrl={avatarUrl}
          avatarPreview={avatarPreview}
          userId={user?.id || ''} 
          onAvatarChange={handleAvatarChange}
          isUploading={isSaving}
        />
        
        {avatarFile && (
          <div className="mt-2">
            <span className="text-sm text-gray-600">{t('clientProfile.newAvatarSelected')}</span>
            <p className="text-xs text-gray-500">{t('clientProfile.willBeUploaded')}</p>
          </div>
        )}
      </div>
      
      <div className="space-y-6">
        <PersonalInfoForm
          defaultValues={{
            first_name: profile?.first_name || "",
            last_name: profile?.last_name || "",
            email: user?.email || "",
            phone_number: profile?.phone_number || ""
          }}
          onSubmit={() => {}}
          isSubmitting={isSaving}
        />
        
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-medium mb-4">{t('clientProfile.preferences')}</h3>
          
          <PreferencesForm
            defaultValues={{
              timezone: profile?.timezone || "UTC+00:00",
              preferred_language: profile?.preferred_language || "en"
            }}
            onSubmit={() => {}}
            isSubmitting={isSaving}
          />
        </div>
      </div>
    </ProfileFormContainer>
  );
};

export default ClientProfile;
